import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { AlertTriangle, Clock, CreditCard, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Abonelik uyari seridi.
 *
 * Kullanici deneme suresinin bittigini, odemenin geciktigini veya hesabin
 * askiya alindigini ancak bir islem YAPMAYA CALISIRKEN 402/403 duvarina
 * carparak ogreniyordu. Bu serit ayni bilgiyi is akisini kesmeden, uyarinin
 * ise yarayacagi zamanda gosteriyor.
 *
 * Rol farketmeksizin gorunur: teknisyen de denemenin bittigini bilmeli.
 * Ama COZUMU yalnizca yonetici uygulayabilir; digerlerine dugme yerine
 * "firma yoneticinize bildirin" yaziyor.
 */

/**
 * Sunucudaki PAST_DUE_GRACE_DAYS varsayilani
 * (bkz. server/services/subscriptionService.js -> pastDueGraceDays).
 * Sunucu bu sureyi ortam degiskeniyle degistirebilir; deger yalnizca
 * "yaklasik kac gun kaldi" demek icin kullaniliyor, yaptirim sunucuda.
 */
const ASKI_ONCESI_EK_SURE_GUN = 7;

/** Kapatma bilgisi: degeri kapatildigi GUN (uyari ertesi gun geri gelir) */
const KAPATMA_ANAHTARI = 'abonelik_uyari_kapatildi';

/* ------------------------------------------------------------------ */
/* Aski durumu                                                         */
/* ------------------------------------------------------------------ */

/**
 * Kiracinin askida oldugu hicbir uc noktada alan olarak donmuyor: bilgi
 * yalnizca istekler 403 TENANT_SUSPENDED aldiginda ortaya cikiyor
 * (/api/billing ve /api/auth acik kaldigi icin abonelik sorgusu 403 almaz).
 * Bu yuzden durum kesiciden toplanip abone bilesenlere dagitiliyor.
 *
 * Deger sekmeye ozel olarak saklaniyor: 403 alan istek kullaniciyi tam
 * sayfa yenilemesiyle /subscription'a atabiliyor (bkz. AuthContext), boyle
 * bir yenilemeden sonra bellekte tutulan bayrak kaybolurdu.
 */
const ASKI_ANAHTARI = 'kiraci_askida';

/** Askidayken de calisan uclar: basarili yanitlari kiracinin acildigini KANITLAMAZ */
const ASKIDA_ACIK_ONEKLER = ['/api/billing', '/api/auth', '/api/public'];

const askidaAcikMi = (url) => ASKIDA_ACIK_ONEKLER.some((onek) => String(url || '').includes(onek));

const askiOku = () => {
  try {
    return sessionStorage.getItem(ASKI_ANAHTARI) === '1';
  } catch (_) {
    return false;
  }
};

let askidaMi = askiOku();
const askiAboneleri = new Set();

const askiyiYaz = (deger) => {
  if (askidaMi === deger) return;
  askidaMi = deger;
  try {
    if (deger) sessionStorage.setItem(ASKI_ANAHTARI, '1');
    else sessionStorage.removeItem(ASKI_ANAHTARI);
  } catch (_) { /* depolama kapaliysa bellekteki deger yeterli */ }
  askiAboneleri.forEach((bildir) => bildir(deger));
};

/**
 * Kesici MODUL DUZEYINDE tek sefer kuruluyor; her bilesen kendi kesicisini
 * eklerse ayni hata birden fazla kez islenir ve kayitlar birikir.
 */
axios.interceptors.response.use(
  (yanit) => {
    // Kisitli bir ucun cevap vermesi = kiraci artik askida degil
    // (odeme alindiginda sunucu hesabi otomatik aciyor)
    if (askidaMi && !askidaAcikMi(yanit.config?.url)) askiyiYaz(false);
    return yanit;
  },
  (hata) => {
    if (hata.response?.status === 403 && hata.response?.data?.code === 'TENANT_SUSPENDED') {
      askiyiYaz(true);
    }
    return Promise.reject(hata);
  }
);

/** Kiraci askida mi? (Abonelik sayfasi da ayni bilgiyi kullaniyor.) */
export const useKiraciAskida = () => {
  const [deger, setDeger] = useState(askidaMi);

  useEffect(() => {
    askiAboneleri.add(setDeger);
    // Abone olana kadar gelmis bir 403 kacmasin
    setDeger(askidaMi);
    return () => { askiAboneleri.delete(setDeger); };
  }, []);

  return deger;
};

/* ------------------------------------------------------------------ */
/* Yardimcilar                                                         */
/* ------------------------------------------------------------------ */

/** Depolama kapali olabilir (gizli sekme); okuma hicbir zaman patlamamali */
const kapatmaGunu = () => {
  try {
    return localStorage.getItem(KAPATMA_ANAHTARI);
  } catch (_) {
    return null;
  }
};

/** Kapatmanin gun bazinda tutulmasi icin YEREL tarih anahtari */
const bugunAnahtari = () => {
  const d = new Date();
  const ay = String(d.getMonth() + 1).padStart(2, '0');
  const gun = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${ay}-${gun}`;
};

/** Verilen tarihe kalan tam gun; gecmisse negatif */
const kalanGun = (tarih) => {
  if (!tarih) return null;
  const ms = new Date(tarih).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.ceil((ms - Date.now()) / 86400000);
};

/**
 * Ton renkleri.
 *
 * Sari ve kirmizi tema belirteclerinden geliyor; ARADAKI turuncu icin
 * belirtec yok, o yuzden burada tanimlaniyor (karanlik tema dahil) —
 * gecikmis odeme ile askiya alinmis hesap ayni renkte gorunmemeli.
 */
const stiller = `
  .abonelik-uyari {
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
    padding: 12px 14px; margin-bottom: 16px;
    border: 1px solid var(--uyari-kenar); border-left-width: 4px;
    border-radius: var(--r-sm, 10px);
    background: var(--uyari-arka);
  }
  .abonelik-uyari[data-ton="deneme"] {
    --uyari-arka: var(--warning-soft); --uyari-kenar: var(--warning); --uyari-yazi: var(--warning-text);
  }
  .abonelik-uyari[data-ton="gecikme"] {
    --uyari-arka: #FFF1E7; --uyari-kenar: #EA580C; --uyari-yazi: #C2410C;
  }
  .dark .abonelik-uyari[data-ton="gecikme"] {
    --uyari-arka: rgba(234, 88, 12, 0.16); --uyari-kenar: #FB923C; --uyari-yazi: #FDBA74;
  }
  .abonelik-uyari[data-ton="kritik"] {
    --uyari-arka: var(--danger-soft); --uyari-kenar: var(--danger); --uyari-yazi: var(--danger-text);
  }
  .abonelik-uyari-govde { flex: 1 1 260px; min-width: 0; color: var(--uyari-yazi); }
  .abonelik-uyari-baslik { font-size: 13.5px; font-weight: 700; }
  .abonelik-uyari-metin { font-size: 12.5px; margin-top: 2px; opacity: 0.92; }
  .abonelik-uyari-kapat {
    width: 30px; height: 30px; flex-shrink: 0; border: none; background: transparent;
    border-radius: 8px; cursor: pointer; color: var(--uyari-yazi);
    display: flex; align-items: center; justify-content: center;
  }
  .abonelik-uyari-kapat:hover { background: rgba(0, 0, 0, 0.06); }
`;

/* ------------------------------------------------------------------ */

const AbonelikUyariBanner = () => {
  const { user } = useAuth();
  const askida = useKiraciAskida();
  const konum = useLocation();
  const [kapatildi, setKapatildi] = useState(() => kapatmaGunu() === bugunAnahtari());

  // Abonelik sayfasiyla ayni onbellek anahtari: iki yerde ayri istek atilmaz
  const { data } = useQuery(
    'subscription',
    async () => (await axios.get('/api/billing/subscription')).data,
    {
      enabled: !!user,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  const abonelik = data?.subscription;
  const yonetici = user?.role === 'yonetici';

  let uyari = null;

  if (askida || ['expired', 'cancelled'].includes(abonelik?.status)) {
    uyari = {
      ton: 'kritik',
      ikon: AlertTriangle,
      baslik: 'Hesabınız askıda',
      metin: 'Kayıt ekleme ve güncelleme işlemleri durduruldu. Plan seçip ödeme yapınca hesabınız açılır.',
      dugme: 'Abonelik',
      // Askidayken kapatma yok: kullanici neden hicbir sey yapamadigini
      // gorebilmeli, aksi halde uyariyi kapatip hatalarla bas basa kalir.
      kapatilabilir: false,
    };
  } else if (abonelik?.status === 'past_due') {
    const donemBitisi = kalanGun(abonelik.current_period_end);
    const askiyaKalan = donemBitisi === null ? null : donemBitisi + ASKI_ONCESI_EK_SURE_GUN;
    uyari = {
      ton: 'gecikme',
      ikon: CreditCard,
      baslik: 'Ödemeniz gecikti',
      metin: askiyaKalan === null || askiyaKalan <= 0
        ? 'Hesabınız her an askıya alınabilir.'
        : `${askiyaKalan} gün içinde hesabınız askıya alınacak.`,
      dugme: 'Ödeme yap',
      kapatilabilir: true,
    };
  } else if (abonelik?.status === 'trialing') {
    const denemeKalan = kalanGun(abonelik.trial_ends_at);
    if (denemeKalan !== null && denemeKalan <= 7) {
      uyari = {
        ton: 'deneme',
        ikon: Clock,
        baslik: denemeKalan > 0
          ? `Deneme süreniz ${denemeKalan} gün sonra doluyor`
          : 'Deneme süreniz doldu',
        metin: 'Kesintisiz devam etmek için bir plan seçin.',
        dugme: 'Plan seç',
        kapatilabilir: true,
      };
    }
  }

  if (!uyari) return null;
  if (uyari.kapatilabilir && kapatildi) return null;
  // Abonelik sayfasi ayni bilgiyi zaten daha ayrintili veriyor
  if (konum.pathname.startsWith('/subscription')) return null;

  const Ikon = uyari.ikon;

  const kapat = () => {
    try {
      localStorage.setItem(KAPATMA_ANAHTARI, bugunAnahtari());
    } catch (_) { /* depolama kapaliysa uyari sayfa yenilenene kadar gizli kalir */ }
    setKapatildi(true);
  };

  return (
    <>
      <style>{stiller}</style>
      <div className="abonelik-uyari" data-ton={uyari.ton} role="status" aria-live="polite">
        <Ikon style={{ width: 19, height: 19, flexShrink: 0, color: 'var(--uyari-yazi)' }} />

        <div className="abonelik-uyari-govde">
          <div className="abonelik-uyari-baslik">{uyari.baslik}</div>
          <div className="abonelik-uyari-metin">{uyari.metin}</div>
        </div>

        {yonetici ? (
          <Link
            to="/subscription"
            className="ds-btn ds-btn-sm ds-btn-secondary"
            style={{ flexShrink: 0 }}
          >
            <CreditCard style={{ width: 14, height: 14 }} />
            {uyari.dugme}
          </Link>
        ) : (
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--uyari-yazi)', flexShrink: 0 }}>
            Firma yöneticinize bildirin.
          </span>
        )}

        {uyari.kapatilabilir && (
          <button
            type="button"
            className="abonelik-uyari-kapat"
            onClick={kapat}
            aria-label="Uyarıyı bugünlük kapat"
            title="Bugünlük kapat"
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        )}
      </div>
    </>
  );
};

export default AbonelikUyariBanner;
